/**
 * Google AdSense slots ("Pubblicità") on the voice page. Configuration comes from `/ads.json`
 * (web/public/ads.json), served by the same domain, so it can change without rebuilding the code.
 * See web/public/brand/ADS.md.
 *
 * A slot is shown only when: ads are enabled, the publisher id is real (not the placeholder), the slot
 * has an ad unit id, and the user accepted advertising cookies. Otherwise it renders nothing.
 */

/** Positions on the site, with the size reserved for each. Only the voice page has ads. */
export const AD_SLOTS = {
  /** Voice page, desktop: bottom of the left panel, under "Volume voci" and the notices. Medium rectangle. */
  'voice-sidebar': { width: 300, height: 250 },
  /** Voice page, desktop: under the players grid and "Connesso a N giocatori". Leaderboard, responsive. */
  'voice-bottom': { width: 728, height: 90 },
  /** Voice page, mobile: the bottom banner, at the end of the content. Large mobile banner. */
  'voice-mobile': { width: 320, height: 100 },
} as const;

export type AdSlotId = keyof typeof AD_SLOTS;

export function isAdSlotId(v: string): v is AdSlotId {
  return Object.prototype.hasOwnProperty.call(AD_SLOTS, v);
}

export type AdsenseFormat = 'auto' | 'rectangle' | 'horizontal' | 'vertical';

export interface AdsenseUnit {
  /** Publisher id, "ca-pub-" + 16 digits. */
  client: string;
  /** Ad unit id from the AdSense console (digits). */
  slot: string;
  format: AdsenseFormat;
}

export interface AdsConfig {
  /** Global switch (false in the example file). */
  enabled: boolean;
  /** Publisher id; null when missing, invalid or the placeholder. */
  client: string | null;
  /** Valid, enabled ad units per position. */
  slots: Partial<Record<AdSlotId, AdsenseUnit>>;
}

const CLIENT_RE = /^ca-pub-\d{16}$/;
/** The example publisher id shipped in ads.json: never sent to Google. */
export const PLACEHOLDER_CLIENT = 'ca-pub-0000000000000000';
const FORMATS: readonly AdsenseFormat[] = ['auto', 'rectangle', 'horizontal', 'vertical'];

/** Validates the JSON of `/ads.json`. Invalid entries are dropped; never throws. */
export function parseAdsConfig(raw: unknown): AdsConfig {
  const off: AdsConfig = { enabled: false, client: null, slots: {} };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return off;
  const o = raw as Record<string, unknown>;
  const client = typeof o.client === 'string' && CLIENT_RE.test(o.client) && o.client !== PLACEHOLDER_CLIENT ? o.client : null;
  const cfg: AdsConfig = { enabled: o.enabled === true, client, slots: {} };
  const slots = o.slots && typeof o.slots === 'object' ? (o.slots as Record<string, unknown>) : {};
  if (!client) return cfg;
  for (const [key, value] of Object.entries(slots)) {
    if (!isAdSlotId(key) || !value || typeof value !== 'object') continue;
    const v = value as Record<string, unknown>;
    if (v.enabled === false) continue;
    const slot = typeof v.slot === 'string' ? v.slot : typeof v.slot === 'number' ? String(v.slot) : '';
    if (!/^\d{1,20}$/.test(slot) || /^0+$/.test(slot)) continue;
    const format = FORMATS.includes(v.format as AdsenseFormat) ? (v.format as AdsenseFormat) : 'auto';
    cfg.slots[key] = { client, slot, format };
  }
  return cfg;
}

export const ADS_URL = '/ads.json';

let pending: Promise<AdsConfig | null> | null = null;

/** Loads `/ads.json` once per page load. Missing or broken file (or the SPA's index.html) = no ads. */
export function loadAds(): Promise<AdsConfig | null> {
  if (!pending) {
    pending = (async () => {
      try {
        const res = await fetch(ADS_URL, { cache: 'no-cache', credentials: 'omit' });
        if (!res.ok) return null;
        return parseAdsConfig(await res.json());
      } catch {
        return null;
      }
    })();
  }
  return pending;
}

/** The AdSense unit of a position, or null when ads are off or the position is not configured. */
export function unitFor(cfg: AdsConfig | null, slot: AdSlotId): AdsenseUnit | null {
  if (!cfg || !cfg.enabled || !cfg.client) return null;
  return cfg.slots[slot] ?? null;
}

/** True if at least one position can show AdSense (only then the consent notice is needed). */
export function usesAdsense(cfg: AdsConfig | null): boolean {
  return !!cfg && cfg.enabled && !!cfg.client && Object.keys(cfg.slots).length > 0;
}

/* ------------------------------------------------------------------ advertising consent */

export type AdConsent = 'granted' | 'denied' | null;
export const CONSENT_KEY = 'vc:ads-consent';

let consentCache: AdConsent | undefined;
const consentListeners = new Set<() => void>();

export function getAdConsent(): AdConsent {
  if (consentCache === undefined) {
    let v: string | null = null;
    try {
      v = window.localStorage.getItem(CONSENT_KEY);
    } catch {
      v = null;
    }
    consentCache = v === 'granted' || v === 'denied' ? v : null;
  }
  return consentCache;
}

/** Saves the choice (null = ask again). Without storage the choice lasts until the page reloads. */
export function setAdConsent(v: AdConsent): void {
  consentCache = v;
  try {
    if (v) window.localStorage.setItem(CONSENT_KEY, v);
    else window.localStorage.removeItem(CONSENT_KEY);
  } catch {
    /* storage unavailable */
  }
  for (const l of consentListeners) l();
}

export function subscribeAdConsent(l: () => void): () => void {
  consentListeners.add(l);
  return () => consentListeners.delete(l);
}

/* ------------------------------------------------------------------ AdSense script */

export const ADSENSE_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';

let script: Promise<void> | null = null;

/**
 * Loads adsbygoogle.js once (only called with consent and a configured unit). Rejects when the
 * script is blocked (ad blocker, network, CSP): the slots then collapse.
 */
export function loadAdsenseScript(client: string): Promise<void> {
  if (!script) {
    script = new Promise<void>((resolve, reject) => {
      const s = document.createElement('script');
      s.async = true;
      s.src = `${ADSENSE_SRC}?client=${encodeURIComponent(client)}`;
      s.crossOrigin = 'anonymous';
      s.dataset.testid = 'adsense-script';
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('adsbygoogle blocked'));
      document.head.appendChild(s);
    });
    script.catch(() => {});
  }
  return script;
}

/** Tests only: forget the cached `/ads.json`, the consent and the script. */
export function resetAdsCache(): void {
  pending = null;
  consentCache = undefined;
  script = null;
  document.querySelectorAll('script[data-testid="adsense-script"]').forEach((s) => s.remove());
}
